import React, { createContext, useContext, useState, useEffect } from 'react';
import { getMe, getClientAccess } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clientAccessLoaded, setClientAccessLoaded] = useState(false);
  const [clientAccess, setClientAccess] = useState({
    has_projects: false,
    has_invitations: false,
    projects: [],
    invitations: []
  });

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('knots_token');
    const savedUser = localStorage.getItem('knots_user');
    
    if (token && savedUser) {
      try {
        const response = await getMe();
        setUser(response.data);
        
        // Fetch client access info
        const accessResponse = await getClientAccess();
        setClientAccess(accessResponse.data);
        setClientAccessLoaded(true);
      } catch (error) {
        localStorage.removeItem('knots_token');
        localStorage.removeItem('knots_user');
        setUser(null);
      }
    }
    setLoading(false);
  };

  const loginUser = async (token, userData) => {
    localStorage.setItem('knots_token', token);
    localStorage.setItem('knots_user', JSON.stringify(userData));
    setUser(userData);
    setClientAccessLoaded(false);
    
    // Fetch client access after login
    try {
      const accessResponse = await getClientAccess();
      setClientAccess(accessResponse.data);
    } catch (error) {
      console.error('Failed to fetch client access');
    }
    setClientAccessLoaded(true);
  };

  const logout = () => {
    localStorage.removeItem('knots_token');
    localStorage.removeItem('knots_user');
    setUser(null);
    setClientAccess({
      has_projects: false,
      has_invitations: false,
      projects: [],
      invitations: []
    });
  };

  const isLeader = () => user?.role === 'leader';
  
  const hasProjectAccess = () => isLeader() || clientAccess.has_projects;
  const hasInvitationAccess = () => isLeader() || clientAccess.has_invitations;
  
  // Get client's single project ID if they only have one
  const getClientSingleProjectId = () => {
    if (isLeader()) return null;
    if (clientAccess.projects.length === 1) {
      return clientAccess.projects[0].id;
    }
    return null;
  };
  
  // Get client's single invitation ID if they only have one
  const getClientSingleInvitationId = () => {
    if (isLeader()) return null;
    if (clientAccess.invitations.length === 1) {
      return clientAccess.invitations[0].id;
    }
    return null;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      loginUser, 
      logout, 
      isLeader, 
      checkAuth,
      clientAccess,
      clientAccessLoaded,
      hasProjectAccess,
      hasInvitationAccess,
      getClientSingleProjectId,
      getClientSingleInvitationId
    }}>
      {children}
    </AuthContext.Provider>
  );
};
